import { z } from 'zod';
import { USER_ROLES, ORDER_STATUS, FILE_ROUTE_TYPE, FILE_STATUS, CACHE_STATE, PRINT_ACTION, PRINT_JOB_STATUS, SUBSCRIPTION_STATUS, COLOR_MODE, MAX_MESSAGE_LENGTH, } from './constants.js';
export const userRoleSchema = z.enum([
    USER_ROLES.SUPERADMIN,
    USER_ROLES.ADMIN,
    USER_ROLES.SHOPKEEPER,
    USER_ROLES.AGENT,
]);
export const orderStatusSchema = z.enum([
    ORDER_STATUS.PENDING,
    ORDER_STATUS.PRINTING,
    ORDER_STATUS.COMPLETED,
    ORDER_STATUS.EXPIRED,
    ORDER_STATUS.CANCELLED,
    ORDER_STATUS.ARCHIVED,
]);
export const fileRouteTypeSchema = z.enum([
    FILE_ROUTE_TYPE.DIRECT,
    FILE_ROUTE_TYPE.MANUAL,
]);
export const fileStatusSchema = z.enum([
    FILE_STATUS.ACTIVE,
    FILE_STATUS.PRINTING,
    FILE_STATUS.PRINTED,
    FILE_STATUS.REVOKED,
]);
export const cacheStateSchema = z.enum([
    CACHE_STATE.NONE,
    CACHE_STATE.DOWNLOADING,
    CACHE_STATE.CACHED,
    CACHE_STATE.FAILED,
]);
export const printActionSchema = z.enum([
    PRINT_ACTION.PRINT,
    PRINT_ACTION.SEND_TO_DESKTOP,
    PRINT_ACTION.CANCEL,
]);
export const printJobStatusSchema = z.enum([
    PRINT_JOB_STATUS.QUEUED,
    PRINT_JOB_STATUS.PRINTING,
    PRINT_JOB_STATUS.DONE,
    PRINT_JOB_STATUS.FAILED,
    PRINT_JOB_STATUS.CANCELLED,
]);
export const subscriptionStatusSchema = z.enum([
    SUBSCRIPTION_STATUS.ACTIVE,
    SUBSCRIPTION_STATUS.GRACE,
    SUBSCRIPTION_STATUS.EXPIRED,
    SUBSCRIPTION_STATUS.SUSPENDED,
]);
export const colorModeSchema = z.enum([
    COLOR_MODE.BW,
    COLOR_MODE.COLOR,
]);
// Auth Schemas
export const loginSchema = z
    .object({
    username: z.string().min(1).optional(),
    email: z.string().optional(),
    password: z.string().min(6),
    deviceLabel: z.string().optional(),
    deviceId: z.string().optional(),
    targetPortal: z.enum(['shop', 'admin', 'superadmin']).optional(),
})
    .refine((data) => Boolean(data.username || data.email), {
    message: 'Username or email is required',
});
export const totpVerifySchema = z.object({
    email: z.string().optional(),
    username: z.string().optional(),
    token: z.string().min(6).max(8),
});
export const changePasswordSchema = z.object({
    currentPassword: z.string(),
    newPassword: z.string().min(8),
});
// Admin / Shop Schemas
export const shopFeaturesSchema = z.object({
    adminMessagingAllowed: z.boolean().optional().default(true),
    allMessagesEnabled: z.boolean().optional().default(true),
    orderReceivedEnabled: z.boolean().optional().default(true),
    printingConfirmationEnabled: z.boolean().optional().default(true),
    expiryNoticeEnabled: z.boolean().optional().default(true),
    welcomeMessageEnabled: z.boolean().optional().default(true),
    orderRevivedNoticeEnabled: z.boolean().optional().default(true),
    turnNextEnabled: z.boolean().optional().default(true),
});
export const createShopSchema = z.object({
    name: z.string().min(1).max(100),
    phone: z.string().min(5).max(25),
    subscriptionMonths: z.number().int().min(1).max(120).default(3).optional(),
    subscriptionDays: z.number().int().min(1).max(3650).optional(),
    shopkeeperUsername: z.string().min(3).max(50).optional(),
    shopkeeperEmail: z.string().email().optional(),
    shopkeeperPassword: z.string().min(6),
    allowedIps: z.array(z.string()).default([]),
    ipLockEnabled: z.boolean().default(false),
    features: shopFeaturesSchema.default({
        adminMessagingAllowed: true,
        allMessagesEnabled: true,
        orderReceivedEnabled: true,
        printingConfirmationEnabled: true,
        expiryNoticeEnabled: true,
        welcomeMessageEnabled: true,
        orderRevivedNoticeEnabled: true,
        turnNextEnabled: true,
    }),
});
export const updateShopSchema = z.object({
    name: z.string().min(1).max(100).optional(),
    phone: z.string().min(5).max(25).optional(),
    allowedIps: z.array(z.string()).optional(),
    ipLockEnabled: z.boolean().optional(),
    features: shopFeaturesSchema.partial().optional(),
});
export const extendSubscriptionSchema = z
    .object({
    months: z.number().int().min(-120).max(120).optional(),
    days: z.number().int().min(-3650).max(3650).optional(),
})
    .refine((data) => (data.months !== undefined && data.months !== 0) ||
    (data.days !== undefined && data.days !== 0), {
    message: 'Either non-zero months or days must be provided',
});
export const manualSubscriptionSchema = z.object({
    validUntil: z.string().datetime(),
    graceDays: z.number().int().min(0).max(30).default(3),
});
export const whatsAppCredentialsSchema = z.object({
    phoneNumberId: z.string().min(1).transform((s) => s.trim()),
    wabaId: z.string().min(1).transform((s) => s.trim()),
    verifyToken: z.string().min(1).transform((s) => s.trim()),
    appSecret: z.string().min(1).transform((s) => s.trim()),
    accessToken: z.string().min(1).transform((s) => s.trim()),
    graphVersion: z.string().default('v21.0').transform((s) => s.trim()),
});
// Message Template Schemas
export const messageTemplateSchema = z
    .string()
    .max(MAX_MESSAGE_LENGTH, `Message must be at most ${MAX_MESSAGE_LENGTH} characters`);
export const updateMessagesConfigSchema = z.object({
    welcomeMessage: messageTemplateSchema.optional(),
    orderReceived: messageTemplateSchema.optional(),
    printingConfirmation: messageTemplateSchema.optional(),
    expiryNotice: messageTemplateSchema.optional(),
});
// Order and Job Options Schemas
export const filePrintOptionsSchema = z.object({
    duplex: z.boolean().optional(),
    copies: z.number().int().min(1).max(99).optional(),
    colorMode: colorModeSchema.optional(),
    pageRange: z.string().optional(),
    pageCount: z.number().int().min(1).max(9999).optional(),
    orientation: z.enum(['auto', 'portrait', 'landscape']).optional(),
    fitToPage: z.boolean().optional(),
});
export const updateFilePrintOptionsSchema = filePrintOptionsSchema;
export const updateOrderOptionsSchema = z.object({
    copies: z.number().int().min(1).max(99).optional(),
    duplex: z.boolean().optional(),
    colorMode: colorModeSchema.optional(),
    pageRange: z.string().optional(),
    pageCount: z.number().int().min(1).max(9999).optional(),
    orientation: z.enum(['auto', 'portrait', 'landscape']).optional(),
    fitToPage: z.boolean().optional(),
});
export const turnNextOrderSchema = z.object({
    notifyCustomer: z.boolean().optional().default(false),
});
export const createPrintJobSchema = z.object({
    action: printActionSchema,
    copies: z.number().int().min(1).max(99).default(1),
    duplex: z.boolean().default(true), // Default both-side print
    colorMode: colorModeSchema.default(COLOR_MODE.BW),
    fileOptions: z.record(z.string(), filePrintOptionsSchema).optional(),
    printerId: z.string().optional(),
    printerName: z.string().optional(),
});
// Agent Communication Schemas
export const agentTokenRequestSchema = z.object({
    shopId: z.string().uuid(),
    agentKey: z.string().min(10),
});
export const agentJobResultSchema = z.object({
    jobId: z.string().uuid(),
    orderId: z.string().uuid(),
    status: z.enum(['DONE', 'FAILED']),
    error: z.string().nullish(),
});
export const agentFileStateSchema = z.object({
    fileId: z.string().uuid(),
    cacheState: cacheStateSchema,
    pageCount: z.number().int().nonnegative().nullish(),
});
export const agentHeartbeatSchema = z.object({
    version: z.string().optional(),
});
// Shop Settings & Inventory Schemas
export const updateSystemConfigSchema = z.object({
    bwPricePerPage: z.number().nonnegative().optional(),
    bwDoublePricePerPage: z.number().nonnegative().optional(),
    colorPricePerPage: z.number().nonnegative().optional(),
    colorDoublePricePerPage: z.number().nonnegative().optional(),
    colorPrintEnabled: z.boolean().optional(),
    currency: z.string().min(1).max(10).optional(),
    timezone: z.string().optional(),
    utcOffsetMinutes: z.number().int().optional(),
});
export const updateInventorySchema = z.object({
    reams: z.number().nonnegative().optional(),
    sheetsPerReam: z.number().int().positive().optional(),
    lowStockThresholdReams: z.number().nonnegative().optional(),
});
export const addReamSchema = z.object({
    count: z.number().int().positive().default(1),
});
export const platformConfigSchema = z.object({
    diskCapGb: z.number().int().positive().default(180),
    cleanupHighWatermarkPct: z.number().min(50).max(99).default(90),
    cleanupTargetPct: z.number().min(30).max(90).default(80),
    detailLogDays: z.number().int().positive().default(2),
    finishedOrderRetentionDays: z.number().int().positive().default(2),
    defaultGraceDays: z.number().int().nonnegative().default(3),
    orderExpiryHours: z.number().int().positive().default(23),
});
//# sourceMappingURL=schemas.js.map